import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Plus, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";

export default function Blog() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const isAdmin = useAdmin();

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog"],
    queryFn: endpoints.listBlogPosts,
  });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="blog-page">
      <Seo
        title={isRTL ? "مدوّنة علم القهوة المختصة" : "Coffee Science Blog"}
        description={isRTL
          ? "مقالات عميقة في علوم القهوة المختصة بالعربية: التحميص، الاستخلاص، التذوق، عجلة النكهات، التحضير، أخبار صناعة القهوة العالمية."
          : "Deep articles on specialty coffee science: roasting, extraction, sensory, flavor wheel, brewing, and global coffee industry news."}
        keywords="مدوّنة القهوة, مقالات قهوة, علم القهوة, مقالات تحميص قهوة, مقالات استخلاص قهوة, coffee blog, coffee articles"
        path="/blog"
      />
      <div className="flex items-end justify-between gap-4 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">
            {isRTL ? "علم القهوة" : "Coffee Science"}
          </div>
          <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-tight`}>
            {isRTL ? "مدوّنة عبدالله الكاساني" : "The Coffee Journal"}
          </h1>
          <p className="mt-4 text-[#5C544F] max-w-2xl text-base lg:text-lg leading-relaxed">
            {isRTL
              ? "مقالات وقصص ودروس مفصّلة عن عالم القهوة المتخصصة — من زراعة البن إلى الفنجان الذي بين يديك."
              : "Long-form articles, stories, and deep dives into specialty coffee — from farm to cup."}
          </p>
        </div>
        {isAdmin && (
          <Link to="/blog/new" data-testid="new-post-btn">
            <Button className="rounded-full bg-[#2A2421] text-[#F9F6F0]">
              <Plus className="w-4 h-4 me-2" /> {isRTL ? "مقال جديد" : "New post"}
            </Button>
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="text-[#5C544F]">{t.common.loading}</div>
      ) : posts.length === 0 ? (
        <div className="soft-card rounded-2xl p-12 text-center" data-testid="blog-empty">
          <BookOpen className="w-12 h-12 mx-auto text-[#C86240]/40 mb-4" />
          <div className={`${serifClass} text-2xl text-[#2A2421] mb-2`}>
            {isRTL ? "قريباً..." : "Coming soon..."}
          </div>
          <p className="text-[#5C544F]">
            {isRTL
              ? "أول مقال يأتي قريباً. تابع موقعنا أو تابعنا على Instagram."
              : "The first article is on its way. Stay tuned!"}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8" data-testid="blog-grid">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`post-card-${p.slug}`}
              className="soft-card rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform group block"
            >
              {p.cover_image && (
                <div className="aspect-[16/9] overflow-hidden bg-[#F9F6F0]">
                  <img
                    src={p.cover_image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                  />
                </div>
              )}
              <div className="p-6">
                {p.tags?.length > 0 && (
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">
                    {p.tags.slice(0, 2).join(" · ")}
                  </div>
                )}
                <h2 className={`${serifClass} text-2xl lg:text-3xl text-[#2A2421] leading-tight mb-3`}>
                  {lang === "ar" ? p.title_ar : p.title_en}
                </h2>
                <p className="text-[#5C544F] leading-relaxed line-clamp-3">
                  {lang === "ar" ? p.excerpt_ar : p.excerpt_en}
                </p>
                <div className="mt-4 text-xs text-[#5C544F]">
                  {p.author_name} ·{" "}
                  {new Date(p.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
                    year: "numeric", month: "long", day: "numeric",
                  })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
